import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowRight, CheckCircle2, XCircle, MailCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Snowfall, TwinkleStars } from "@/components/Snowfall";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-3 pl-11 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none transition-colors focus:border-amber-400/70";

const btnCls =
  "flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-600 px-6 py-3.5 font-bold text-[#1A1204] shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-amber-500/30 disabled:opacity-60";

const errorCls = "rounded-lg border border-red-400/40 bg-red-900/20 px-4 py-2.5 text-sm text-red-200";

const Shell = ({ children, testid }) => (
  <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#06150F] px-4 py-12 text-[#FAF6EE]">
    <Snowfall />
    <TwinkleStars />
    <motion.div
      aria-hidden
      className="pointer-events-none absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-red-800/25 blur-3xl"
      animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
      transition={{ repeat: Infinity, duration: 18, ease: "easeInOut" }}
    />
    <motion.div
      aria-hidden
      className="pointer-events-none absolute -right-32 bottom-1/4 h-96 w-96 rounded-full bg-emerald-700/20 blur-3xl"
      animate={{ x: [0, -50, 0], y: [0, 45, 0] }}
      transition={{ repeat: Infinity, duration: 22, ease: "easeInOut" }}
    />
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative z-10 w-full max-w-md"
    >
      <div
        data-testid={testid}
        className="rounded-3xl border border-amber-500/25 bg-[#0A1D15]/80 p-8 shadow-[0_30px_80px_rgba(0,0,0,0.55)] backdrop-blur-xl sm:p-10"
      >
        <Link to="/" className="mb-8 flex items-center justify-center gap-3" data-testid="recovery-logo">
          <Logo className="h-11 w-11" />
          <span className="font-display text-2xl font-bold text-amber-100">Avvento d'Oro</span>
        </Link>
        {children}
      </div>
    </motion.div>
  </div>
);

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err, "Richiesta non riuscita. Riprova."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell testid="forgot-password-card">
      <h1 className="font-hero text-center text-3xl font-semibold text-amber-100">Password dimenticata?</h1>
      <p className="mt-2 text-center text-sm text-emerald-100/65">
        Nessun problema: ti inviamo un link per reimpostarla.
      </p>
      {done ? (
        <div className="mt-8 text-center" data-testid="forgot-password-success">
          <MailCheck className="mx-auto h-12 w-12 text-emerald-300" />
          <p className="mt-4 text-sm leading-relaxed text-emerald-100/80">
            Se l'email è registrata, riceverai a breve un messaggio con il link per reimpostare la
            password. Controlla anche la cartella spam.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-password-form">
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-400/60" />
            <input
              data-testid="forgot-email-input"
              type="email"
              required
              placeholder="La tua email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
          </div>
          {error && (
            <p data-testid="forgot-error" className={errorCls}>
              {error}
            </p>
          )}
          <button type="submit" data-testid="forgot-submit-button" disabled={busy} className={btnCls}>
            {busy ? "Invio in corso..." : "Invia il link di reset"} <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      )}
      <p className="mt-6 text-center text-sm text-emerald-100/60">
        Ti è tornata in mente?{" "}
        <Link to="/accedi" data-testid="forgot-login-link" className="font-semibold text-amber-300 hover:text-amber-200">
          Torna al login
        </Link>
      </p>
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Le due password non coincidono.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err, "Reset non riuscito. Richiedi un nuovo link."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell testid="reset-password-card">
      <h1 className="font-hero text-center text-3xl font-semibold text-amber-100">Nuova password</h1>
      <p className="mt-2 text-center text-sm text-emerald-100/65">
        Scegli una password sicura di almeno 8 caratteri.
      </p>
      {done ? (
        <div className="mt-8 text-center" data-testid="reset-password-success">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-300" />
          <p className="mt-4 text-sm leading-relaxed text-emerald-100/80">
            Password aggiornata! Ora puoi accedere con le nuove credenziali.
          </p>
          <Link to="/accedi" data-testid="reset-goto-login" className={`${btnCls} mt-6`}>
            Vai al login <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : !token ? (
        <p data-testid="reset-missing-token" className={`${errorCls} mt-8`}>
          Link non valido: manca il token. Richiedi un nuovo reset dalla pagina di login.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-password-form">
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-400/60" />
            <input
              data-testid="reset-password-input"
              type="password"
              required
              minLength={8}
              placeholder="Nuova password (min. 8 caratteri)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-400/60" />
            <input
              data-testid="reset-confirm-input"
              type="password"
              required
              minLength={8}
              placeholder="Ripeti la password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputCls}
            />
          </div>
          {error && (
            <p data-testid="reset-error" className={errorCls}>
              {error}
            </p>
          )}
          <button type="submit" data-testid="reset-submit-button" disabled={busy} className={btnCls}>
            {busy ? "Salvataggio..." : "Salva la nuova password"} <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      )}
    </Shell>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState("error");
      return;
    }
    api
      .post("/auth/verify-email", { token })
      .then(() => setState("ok"))
      .catch(() => setState("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Shell testid="verify-email-card">
      {state === "loading" && (
        <div className="py-6 text-center" data-testid="verify-email-loading">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-amber-400/30 border-t-amber-400" />
          <p className="mt-4 text-sm text-emerald-100/70">Sto confermando la tua email...</p>
        </div>
      )}
      {state === "ok" && (
        <div className="text-center" data-testid="verify-email-success">
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-300" />
          <h1 className="font-hero mt-4 text-3xl font-semibold text-amber-100">Email confermata!</h1>
          <p className="mt-3 text-sm leading-relaxed text-emerald-100/75">
            Il tuo indirizzo è verificato: le caselle del tuo Calendario dell'Avvento sono
            sbloccate. Buon viaggio verso la Vigilia!
          </p>
          <button
            type="button"
            data-testid="verify-goto-dashboard"
            onClick={() => window.location.assign("/dashboard")}
            className={`${btnCls} mt-7`}
          >
            Vai alla tua area personale <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
      {state === "error" && (
        <div className="text-center" data-testid="verify-email-error">
          <XCircle className="mx-auto h-14 w-14 text-red-300" />
          <h1 className="font-hero mt-4 text-3xl font-semibold text-amber-100">Link non valido</h1>
          <p className="mt-3 text-sm leading-relaxed text-emerald-100/75">
            Il link di conferma non è valido o è già stato utilizzato. Accedi alla tua area
            personale: da lì puoi richiedere un nuovo invio dell'email di conferma.
          </p>
          <Link to="/accedi" data-testid="verify-goto-login" className={`${btnCls} mt-7`}>
            Vai al login <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </Shell>
  );
}
